import {
    ShieldStar,
    TestTube,
    PawPrint,
    Baby,
    Drop,
    Leaf,
} from "@phosphor-icons/react";
import { Chapter, Reveal } from "@/components/site/Reveal";

const POINTS = [
    {
        icon: ShieldStar,
        title: "PFAS-free",
        desc: "Zero per-/polyfluoroalkyl substances (so-called 'forever chemicals'). Our fibers and backing are independently certified PFAS-free.",
    },
    {
        icon: TestTube,
        title: "Independently lab-tested",
        desc: "Every roll meets third-party testing standards for lead, heavy metals, phthalates, and VOCs — documentation available on request.",
    },
    {
        icon: PawPrint,
        title: "Pet-safe",
        desc: "Non-toxic, antimicrobial fibers with permeable backing — no chemical irritants, drains quickly, easy to rinse clean.",
    },
    {
        icon: Baby,
        title: "Kid-safe",
        desc: "Soft underfoot with shock-absorbent infill options. Safe for bare feet, tumbles, and play — no fertilizers or pesticides ever needed.",
    },
];

export const Safety = () => {
    return (
        <section
            id="safety"
            data-testid="safety-section"
            className="relative overflow-hidden py-24 md:py-36"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-brand-accent/10 blur-3xl"
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-brand-moss/10 blur-3xl"
            />

            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <Chapter
                    index="03"
                    label="Safety & materials"
                    testid="safety-chapter"
                    title={
                        <>
                            PFAS-free, lab-tested, and{" "}
                            <span className="italic text-brand-accent">safe for the ones you love</span>.
                        </>
                    }
                    copy="We're picky about what we install because we know what's going under your kids' bare feet and your dog's paws."
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {POINTS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <Reveal key={p.title} delay={i * 0.07} className="h-full">
                                <div
                                    data-testid={`safety-point-${i}`}
                                    className="spotlight group relative h-full overflow-hidden rounded-2xl border border-brand-border bg-white p-6 transition-colors duration-500 hover:border-brand-accent/40"
                                >
                                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-accent/10 ring-1 ring-brand-accent/30">
                                        <Icon
                                            size={22}
                                            weight="duotone"
                                            className="text-brand-accent"
                                        />
                                    </div>
                                    <h3 className="font-display mt-5 text-xl font-medium text-brand-text">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                        {p.desc}
                                    </p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal delay={0.15}>
                    <div
                        data-testid="safety-water-note"
                        className="mt-6 flex flex-col items-start gap-4 rounded-2xl border border-brand-border bg-white p-6 sm:flex-row sm:items-center"
                    >
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand-accent/10 ring-1 ring-brand-accent/30">
                            <Drop
                                size={20}
                                weight="duotone"
                                className="text-brand-accent"
                            />
                        </div>
                        <p className="text-sm leading-relaxed text-brand-muted md:text-base">
                            <span className="font-semibold text-brand-text">
                                Drains 30+ inches of water per hour.
                            </span>{" "}
                            Idaho rain, sprinkler overspray, and pet cleanup all
                            wash right through — no puddles, no mildew, no muddy
                            paws.
                        </p>
                        <div className="inline-flex shrink-0 items-center gap-3 rounded-full border border-brand-moss/30 bg-brand-moss/10 px-4 py-2.5 sm:ml-auto">
                            <Leaf
                                size={16}
                                weight="duotone"
                                className="text-brand-moss"
                            />
                            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-moss">
                                No pesticides · No runoff
                            </span>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
